import { useMemo } from 'react';
import { Table } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchUserCardexes } from '@/services/dormitoryLegacy.service';
import { Flex, FoldingList, LoadingSpin, PageContainer, PageError, Permission } from '@/components';
import { Routes } from '@/routes';
import { ColumnBuilder } from '@/utils';
import { dormitorySolitaryStatus } from '../helper';
import parseError from '@/utils/parseError';
import { useTranslation } from 'react-i18next';
import { useDevice } from '@/hooks';

export function DormitoryCardexes() {

  const navigate = useNavigate();
  const [t] = useTranslation(['common', 'dormitory']);

  const { isDesktop } = useDevice();

  const columns = useMemo(() => {
    return ColumnBuilder
      .create()
      .align('center')
      .setItems([
        { key: 'semester', title: t('common:labels.semester') },
        { key: 'name', title: t('dormitory:labels.dormitory') },
        { key: 'block', title: t('dormitory:labels.block') },
        { key: 'floor', title: t('dormitory:labels.floor') },
        { key: 'room', title: t('dormitory:labels.room') },
        { key: 'capacity', title: t('common:labels.capacity') },
        { key: 'status', title: t('common:labels.status') },
        { key: 'address', title: t('dormitory:labels.address') },
        { key: 'contactInfo', title: t('dormitory:labels.dormitory-contact-info') },
        // { key: 'actions', title: '' },
      ])
      .build();
  }, [t]);

  const { data, error, isFetching } = useQuery({
    queryKey: ['/eskans/cardexes'],
    queryFn: () => fetchUserCardexes()
  });

  const { desktopRows, mobileRows } = useMemo(() => {
    // const currentDate = parse(format(new Date(), 'yyyy-MM-dd HH:mm:ss'), 'yyyy-MM-dd HH:mm:ss', new Date())

    const filteredRows = data?.payload ?? [];

    // TODO: do not accept filter for now
    // const filteredRows = data?.payload.filter((item) => {
    //   const itemSemesterStartDate = parse(format(item.semester.startDate, 'yyyy-MM-dd HH:mm:ss'), 'yyyy-MM-dd HH:mm:ss', new Date())
    //   return isBefore(itemSemesterStartDate, currentDate)
    // });

    // const filteredData = data.payload.filter(item => item.semester.startDate)
    return {
      desktopRows: (filteredRows ?? []).map(row => ({
        semester: `${row?.semester?.period?.name} ${row?.semester?.name}`,
        name: row?.dormitory?.name,
        address: row?.dormitory?.address,
        contactInfo: (row?.dormitory?.contactInfo ?? '').split('،').map((x, i) => <div key={i}>{x.trim()}<br /></div>),
        block: row?.dormitoryBlock?.name,
        floor: row?.room?.dormitoryFloor?.name,
        room: row?.room?.code,
        status: dormitorySolitaryStatus(row?.status as any, t),
        capacity: row?.room?.roomSize,
        actions: <></>,
      })),
      mobileRows: (filteredRows ?? []).map((row) => ({
        description: dormitorySolitaryStatus(row?.status as any, t),
        title: `${row?.semester?.period?.name} ${row?.semester?.name} - ${row?.dormitory.name}`,
        attrs: {
          semester: {
            title: t('common:labels.semester'),
            value: `${row?.semester?.period?.name} ${row?.semester?.name}`
          },
          name: {
            title: t('dormitory:labels.dormitory'),
            value: row?.dormitory?.name
          },
          address: {
            title: t('dormitory:labels.address'),
            value: row?.dormitory?.address
          },
          contactInfo: {
            title: t('dormitory:labels.dormitory-contact-info'),
            value: (row?.dormitory?.contactInfo ?? '').split('،').map((x, i) => <div key={i}>{x.trim()}<br /></div>)
          },
          block: {
            title: t('dormitory:labels.block'),
            value: row?.dormitoryBlock?.name
          },
          floor: {
            title: t('dormitory:labels.floor'),
            value: row?.room?.dormitoryFloor?.name
          },
          room: {
            title: t('dormitory:labels.room'),
            value: row?.room?.code
          },
          status: {
            title: t('common:labels.status'),
            value: dormitorySolitaryStatus(row?.status as any, t)
          },
          capacity: {
            title: t('common:labels.capacity'),
            value: row?.room?.roomSize
          },
        }
      }))
    };
  }, [data?.payload, t]);

  const pageTitle = t('dormitory:labels.cardex-accommodation');

  if (isFetching) {
    return <LoadingSpin fillHeight />;
  }

  if (error) {
    return <PageError title={pageTitle} content={parseError(error)} action={() => navigate(Routes.Dormitory)} />;
  }

  return (
    <Permission required="ROLE_ESKAN_ALGORITM_STUDENT">
      <PageContainer displayFlex title={pageTitle}>
        {isDesktop() ?
          (
            <Flex className="w-full" direction="column">
              <Table
                bordered
                size="large"
                direction="rtl"
                className="w-full"
                dataSource={[...desktopRows].reverse()}
                columns={columns}
                loading={isFetching}
                pagination={false}
                scroll={{ x: 800 }}
              />
            </Flex>
          ) :
          <Flex className="w-full h-full" direction="column">
            <FoldingList
              showEnd={false}
              items={[...mobileRows].reverse() as any}
            />
          </Flex>
        }
      </PageContainer>
    </Permission>
  );
}

export default DormitoryCardexes;
